import { version } from '../../package.json';
import buildConfig from '../build-config.json';
import { PrebuildMessage } from '../message.types';
import { fetch } from '../utility/fetch';
import { HTTP_STATUS } from './constants';
import { Index as ResponseJSON } from '../generated/repro.rails/v3-internal-web_messages';

interface Params {
  userAnnotation?: string;
  idfv: string;
  isFirstSession: boolean;
  token: string;
}

const isKeyOfParams = (prop: string): prop is keyof Params =>
  ['userAnnotation', 'idfv', 'isFirstSession', 'token'].includes(prop);

const buildSearchParams = (params: Params): URLSearchParams => {
  const { userAnnotation = '', idfv, token, isFirstSession } = params;

  const searchParams = new URLSearchParams();

  searchParams.set('user_annotation', userAnnotation);
  searchParams.set('idfv', idfv);
  searchParams.set('is_first_session', isFirstSession ? 'true' : 'false');
  searchParams.set('token', token);
  searchParams.set('sdk_version', version);

  return searchParams;
};

export const fetchMessages = async (params: Params): Promise<PrebuildMessage[]> => {
  if (['idfv', 'token', 'isFirstSession'].some((prop) => isKeyOfParams(prop) && params[prop] === undefined)) {
    return Promise.reject('Aborting Message List API call, due to required parameters missing.');
  }

  if (!navigator.onLine) {
    return Promise.reject(new Error('Aborting messages List API call, due to browser being offline.'));
  }

  const url = new URL(buildConfig.messageListApiURL);
  url.search = buildSearchParams(params).toString();

  const response = await fetch(url.href, {
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors',
  });

  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    throw new Error('Message List API call failed due to authentication error. Check your repro token.');
  }
  if (!response.ok) {
    throw new Error(`Message List API call failed with status ${response.status}.`);
  }
  const json: ResponseJSON = await response.json();

  if (typeof json !== 'object') {
    throw new Error(`Invalid message list response: ${json}`);
  }

  if ('error' in json && !('web_messages' in json)) {
    throw new Error(`Error on message list response: ${json.error}`);
  }

  return json.web_messages ?? [];
};
